import { FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@v2/shared/lib/utils';
import SortSelect from '@v2/shared/components/SortSelect';
import { SortOption, Tab } from './types';

type Props = {
  tab: Tab;
  search: string;
  onSearchChange: (value: string) => void;
  ratings: number[];
  onToggleRating: (stars: number) => void;
  onClearRatings: () => void;
  sortOptions: SortOption[];
  sort: string;
  onSortChange: (value: string) => void;
  isFiltered: boolean;
  onClear: () => void;
};

const CHIP_CLASS = cn(
  'cursor-pointer rounded-full border border-solid px-3.5 py-1.5 text-xs font-semibold',
  'disabled:cursor-not-allowed'
);

/** Complete strings, not a base plus an override — `cn` is clsx and does not resolve conflicts. */
const CHIP_ON = 'border-primary bg-primary text-white';

const CHIP_OFF = 'border-slate-200 bg-white text-slate-600 hover:border-slate-300';

/**
 * Search, star filters and ordering — the three controls the live widget has none of.
 *
 * All three are requests rather than navigations: they update the list in place and
 * never touch the url, so a page that ranks top 3 does not acquire thousands of
 * near-identical filtered addresses. The search form still calls preventDefault on
 * submit, because pressing enter in a text input is a submit whether or not there is
 * anywhere to submit to.
 *
 * The controls do not report the fetch they trigger; ReviewList does, over the reviews
 * themselves. A bar that grows a spinner is a bar that moves the control just clicked.
 */
export default function Toolbar({
  tab,
  search,
  onSearchChange,
  ratings,
  onToggleRating,
  onClearRatings,
  sortOptions,
  sort,
  onSortChange,
  isFiltered,
  onClear,
}: Props) {
  const isProducts = tab === 'products';

  const onSubmit = (event: FormEvent) => {
    // The search is already debounced into the request; this only stops the browser
    // reloading the page when someone hits enter.
    event.preventDefault();
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <form onSubmit={onSubmit} role="search" className="relative min-w-[240px] flex-1 lg:max-w-sm">
        <label htmlFor="review-search" className="sr-only">
          {isProducts ? 'Search magazine reviews' : 'Search customer reviews'}
        </label>

        <input
          id="review-search"
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={isProducts ? 'Search by magazine title…' : 'Search reviews…'}
          className={cn(
            'w-full rounded-lg border border-solid border-slate-200 bg-white',
            'py-2 pl-3 pr-9 text-sm text-slate-900'
          )}
        />

        <span
          aria-hidden
          className="absolute right-0 top-0 flex h-full items-center px-3 text-slate-400"
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} />
        </span>
      </form>

      <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
        <li>
          <button
            type="button"
            aria-pressed={ratings.length === 0}
            disabled={ratings.length === 0}
            // Only the stars. Someone who has searched for a title and then asks for
            // all ratings has not asked to be taken back to every review on the site.
            onClick={onClearRatings}
            className={cn(CHIP_CLASS, ratings.length === 0 ? CHIP_ON : CHIP_OFF)}
          >
            All ratings
          </button>
        </li>

        {[5, 4, 3, 2, 1].map((stars) => {
          const selected = ratings.includes(stars);

          return (
            <li key={stars}>
              <button
                type="button"
                // Chips toggle rather than replace, so four and five stars can be asked
                // for together without the control fighting the visitor.
                aria-pressed={selected}
                onClick={() => onToggleRating(stars)}
                className={cn(CHIP_CLASS, selected ? CHIP_ON : CHIP_OFF)}
              >
                {stars} ★
              </button>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-3 lg:ml-auto">
        {isFiltered && (
          <button
            type="button"
            onClick={onClear}
            className="cursor-pointer border-0 bg-transparent p-0 text-sm text-slate-500 underline"
          >
            Clear
          </button>
        )}

        <SortSelect
          id="review-sort"
          label="Sort by"
          options={sortOptions}
          value={sort}
          onChange={onSortChange}
        />
      </div>
    </div>
  );
}
